import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ClientSidebar from "../components/ClientSidebar";
import { ApiError } from "@/lib/api";
import { listMyReviews, type MyReview } from "@/lib/reviews";

export default function PerfilResenasPage() {
  const { t, i18n } = useTranslation("profile");
  const dateLocale = i18n.language === "es" ? "es-MX" : "en-US";

  const [reviews, setReviews] = useState<MyReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { setReviews(await listMyReviews()); }
    catch (err) {
      setError(err instanceof ApiError ? err.message : t("profile.loadError", { defaultValue: "No pudimos cargar tus reseñas." }));
      setReviews([]);
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ClientSidebar active="reviews" />
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <h1 className="text-lg font-bold text-charcoal mb-4">{t("profile.myReviews")}</h1>

                {error && (
                  <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mb-4">{error}</div>
                )}

                {loading ? (
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
                      <i className="ri-star-line text-3xl" />
                    </div>
                    <p className="text-sm text-gray-500">{t("profile.noReviews")}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((r) => (
                      <div key={r.id} className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <Link to={`/tours/${r.tourSlug}`} className="text-sm font-semibold text-charcoal hover:text-ocean">
                            {r.tourTitle}
                          </Link>
                          <div className="flex items-center gap-0.5 text-coral shrink-0">
                            {Array.from({ length: r.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs" />
                            ))}
                          </div>
                        </div>
                        {r.title ? <p className="text-sm font-medium text-charcoal mb-1">{r.title}</p> : null}
                        <p className="text-sm text-gray-600 mb-2">{r.comment}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>{r.providerName}</span>
                          <span>·</span>
                          <span>{new Date(r.createdAt).toLocaleDateString(dateLocale, { year: "numeric", month: "short", day: "numeric" })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
